import { OnInit, Component, Input, inject } from "@angular/core";
import { TabService } from "@services/tabs/TabService";
import { Pages } from "@constants/pages/Pages";
import { MailMessageModel } from "@app/mailmessages/models/mailMessageModel";
import { MailMessageService } from "@app/mailmessages/service/mailmessage.service";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { ModuleType } from "@enums/ModuleType";
import { TranslocoService, provideTranslocoScope } from "@jsverse/transloco";
import { firstValueFrom } from "rxjs";

@Component({
    selector: "uc-mailmessages-overview",
    templateUrl: "./mailmessages-overview.component.html",
    styleUrls: ["./mailmessages-overview.component.scss"],
    standalone: false,
    providers: [provideTranslocoScope("mailmessages")],
})
export class MailMessagesOverviewComponent implements OnInit {
    private _tabService = inject(TabService);
    private _mmService = inject(MailMessageService);
    private _translocoService = inject(TranslocoService);
    pages = inject(Pages);

    @Input() module: ModuleType;

    config: GenericListConfig;

    // Load the lazy scope JSON before the synchronous translate() calls in setupConfig();
    // otherwise they resolve to the raw key while the scope is still loading.
    async ngOnInit() {
        const [data] = await Promise.all([
            firstValueFrom(this._mmService.getData(this.module)),
            firstValueFrom(
                this._translocoService.load(`mailmessages/${this._translocoService.getActiveLang()}`)
            ),
        ]);

        this.setupConfig(data);
    }

    setupConfig(data: MailMessageModel[]) {
        this.config = <GenericListConfig>{
            name: this._translocoService.translate("mailmessages.overview.title"),
            data: data,
            idProp: "ID",

            allowAdd: false,
            allowEdit: true,
            allowDelete: false,

            fields: [
                {
                    fieldName: "MailTypeDescription",
                    prettyName: this._translocoService.translate("mailmessages.overview.typeHeader"),
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
                {
                    fieldName: "Description",
                    prettyName: this._translocoService.translate(
                        "mailmessages.overview.descriptionHeader"
                    ),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: "Enabled",
                    prettyName: this._translocoService.translate("mailmessages.overview.sendMailHeader"),
                    fieldType: GenericListFieldType.CheckBox,
                },
            ],
            editOverride: (row: MailMessageModel) => {
                this._tabService.generateTab(
                    this.pages.MailMessagesDetail,
                    row.ID,
                    row.MailTypeDescription
                );
            },
        };
    }
}
